import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { formatDayMonthWithWeekday } from "../../utils/formatDate";
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import FlexColumnBox from "../../components/common/FlexBox";
import PeriodDatePicker from "../../components/common/PeriodDatePicker";
import { Hotel } from "../../types/hotel.d";
import HotelSelector from "../../components/common/HotelSelector";
import HotelDataStatusList from "../../components/common/HotelDataStatusList";
import { useAuth } from "../../context/authContext";
import StatService from "../../api/statService";
import HotelService from "../../api/hotelService";
import { downloadCsv, formatCsvFilename } from "../../utils/csvDownload";
import { buildTrafficCsv } from "../../utils/trafficCsv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useDebounce } from "../../utils/useDebounce";

const Traffic = () => {
  const [localStartDate, setLocalStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [localEndDate, setLocalEndDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 6);
    return d;
  });

  const [startDate, setStartDate] = useState<Date>(localStartDate);
  const [endDate, setEndDate] = useState<Date>(localEndDate);

  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const { user } = useAuth();

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const applyDates = useCallback((start: Date, end: Date) => {
    setLocalStartDate(start);
    setLocalEndDate(end);
    setStartDate(start);
    setEndDate(end);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const debouncedSetDates = useCallback((start: Date, end: Date) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setStartDate(start);
      setEndDate(end);
    }, 3000);
  }, []);

  useEffect(() => {
    if (user?.role !== "Admin" && user?.hotel) {
      setSelectedHotel(user.hotel);
      return;
    }
    if (user && (!user.hotel || user.role === "Admin")) {
      setSelectedHotel(null);
    }
  }, [user]);
  const [loading, setLoading] = useState(false);
  const [realData, setRealData] = useState<any[]>([]);
  const [visitorData, setVisitorData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const requestCounter = useRef(0);
  const [hotelDataStatus, setHotelDataStatus] = useState<
    Array<{ id: number; name: string; hasData: boolean }>
  >([]);

  const isYearView = useMemo(() => {
    return (
      startDate.getDate() === 1 &&
      startDate.getMonth() === 0 &&
      endDate.getDate() === 31 &&
      endDate.getMonth() === 11 &&
      startDate.getFullYear() === endDate.getFullYear()
    );
  }, [startDate, endDate]);

  useEffect(() => {
    HotelService.getAllHotels().then(setHotels).catch(console.error);
  }, []);

  const fetchData = useCallback(async () => {
    const currentRequest = ++requestCounter.current;
    setLoading(true);
    setError(null);
    setRealData([]);
    setVisitorData([]);
    setHotelDataStatus([]);
    
    try {
      const hotelId = selectedHotel?.id ?? null;

      const [real, visitors] = await Promise.all([
        StatService.getOccupation(hotelId, startDate, endDate),
        StatService.getVisitors(hotelId, startDate, endDate),
      ]);

      if (currentRequest !== requestCounter.current) return;

      setRealData(real);
      setVisitorData(visitors);

      if (!selectedHotel && hotels.length > 0) {
        const statusList = hotels.map((hotel) => {
          const hasRealData = real.some((item: any) => item.fK_Hotel_ID === hotel.id && item.fK_Hotel_ID > 0);
          const hasVisitorData = visitors.some((item: any) => item.hotelId === hotel.id && item.hotelId > 0);
          return {
            id: hotel.id,
            name: hotel.name,
            hasData: hasRealData || hasVisitorData,
          };
        });
        setHotelDataStatus(statusList);
      }
    } catch (e) {
      if (currentRequest === requestCounter.current) {
        console.error("Erreur de récupération des données : ", e);
        setError("Impossible de charger les données.");
      }
    } finally {
      if (currentRequest === requestCounter.current) {
        setLoading(false);
      }
    }
  }, [selectedHotel, startDate, endDate, hotels]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getMonthlyRows = () => {
    const grouped: { [key: string]: any } = {};

    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setMonth(d.getMonth() + 1)
    ) {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      grouped[key] = {
        label: d.toLocaleDateString("fr-FR", {
          month: "long",
          year: "numeric",
        }),
        excursionists: 0,
        destination: 0,
        available: 0,
        occupied: 0,
      };
    }

    realData.forEach((item) => {
      const date = new Date(item.date);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      if (grouped[key]) {
        grouped[key].available += item.available_Units ?? 0;
        grouped[key].occupied += item.sold_Units ?? 0;
      }
    });

    visitorData.forEach((item) => {
      const date = new Date(item.date);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      if (grouped[key]) {
        if (item.visitorTypeName === "Excursionniste") {
          grouped[key].excursionists += item.total;
        } else if (item.visitorTypeName === "Destination") {
          grouped[key].destination += item.total;
        }
      }
    });

    return Object.entries(grouped).map(([key, val]) => {
      const total = val.excursionists + val.destination;
      const occupancy = val.available
        ? ((val.occupied / val.available) * 100).toFixed(2)
        : "0.00";

      return {
        key,
        label: val.label,
        excursionists: val.excursionists,
        destination: val.destination,
        total,
        available: val.available,
        occupied: val.occupied,
        occupancy,
      };
    });
  };

  const getDailyRows = () => {
    const rows = [];
    let current = new Date(startDate);

    while (current <= endDate) {
      const dateStr = current.toISOString().split("T")[0];

      const real = realData.find((d) => d.date === dateStr);
      const visitors = visitorData.filter((d) => d.date === dateStr);

      const excursionists = visitors
        .filter((v) => v.visitorTypeName === "Excursionniste")
        .reduce((sum, v) => sum + v.total, 0);
      const destination = visitors
        .filter((v) => v.visitorTypeName === "Destination")
        .reduce((sum, v) => sum + v.total, 0);

      const totalVisitors = excursionists + destination;
      const availableNights = real?.available_Units ?? 0;
      const occupiedNights = real?.sold_Units ?? 0;
      const occupancyRate = availableNights
        ? ((occupiedNights / availableNights) * 100).toFixed(2)
        : "0.00";

      rows.push({
        key: dateStr,
        label: formatDayMonthWithWeekday(current, "short"),
        excursionists,
        destination,
        total: totalVisitors,
        available: availableNights,
        occupied: occupiedNights,
        occupancy: occupancyRate,
      });

      current.setDate(current.getDate() + 1);
    }

    return rows;
  };

  const displayRows = isYearView ? getMonthlyRows() : getDailyRows();

  const handleExportCsv = () => {
    if (!displayRows || displayRows.length === 0) return;
    // Determine included hotels list
    const includedHotelNames: string[] = selectedHotel
      ? [selectedHotel.name]
      : hotels.map((h) => h.name);

    const csv = buildTrafficCsv(displayRows, {
      names: includedHotelNames,
      position: "after",
    });
    const filename = formatCsvFilename(
      isYearView ? "achalandage_mensuel" : "achalandage_journalier",
      startDate,
      endDate
    );
    downloadCsv(filename, csv);
  };

  return (
    <FlexColumnBox alignItems="center" gap={3} mt={5} mb={5} width="100%">
      <Box
        width="95%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="relative"
      >
        <Typography variant="h5" sx={{ textAlign: "center", width: "100%" }}>
          Achalandage destination
        </Typography>
        <Box sx={{ position: "absolute", right: 0 }}>
          <Tooltip title="Exporter CSV">
            <span>
              <IconButton color="primary" onClick={handleExportCsv} size="large">
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {(user?.role === "Admin" || !user?.hotel) && (
        <HotelSelector
          hotels={hotels}
          selectedHotel={selectedHotel}
          onChange={setSelectedHotel}
          includeAllOption
          disabled={user?.role !== "Admin" && !!user?.hotel}
        />
      )}

      <PeriodDatePicker
        startDate={localStartDate}
        endDate={localEndDate}
        onChange={(start, end) => {
          setLocalStartDate(start);
          setLocalEndDate(end);
          debouncedSetDates(start, end);
        }}
        onApply={(start, end) => {
          setLocalStartDate(start);
          setLocalEndDate(end);
          applyDates(start, end);
        }}
      />

      {loading ? (
        <CircularProgress sx={{ mt: 4 }} />
      ) : (
        <>
          {error && (
            <Box mt={4}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {!error && realData.length === 0 && visitorData.length === 0 && (
            <Box mt={4}>
              <Alert severity="info">Aucune donnée pour la période sélectionnée.</Alert>
            </Box>
          )}

          {!selectedHotel && hotelDataStatus.length > 0 && (
            <HotelDataStatusList hotels={hotelDataStatus} />
          )}

          {(realData.length > 0 || visitorData.length > 0) && (
            <Paper
              elevation={4}
              sx={{
                width: "95%",
                overflowX: "auto",
                mt: 3,
                boxShadow: 3,
                borderRadius: 2,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headStyle}>Date</TableCell>
                    <TableCell align="right" sx={headStyle}>
                      Excursionniste
                    </TableCell>
                    <TableCell align="right" sx={headStyle}>
                      Destination
                    </TableCell>
                    <TableCell align="right" sx={headStyle}>
                      Total de personnes
                    </TableCell>
                    <TableCell align="right" sx={headStyle}>
                      Nuitées disponibles
                    </TableCell>
                    <TableCell align="right" sx={headStyle}>
                      Nuitées occupées
                    </TableCell>
                    <TableCell align="right" sx={headStyle}>
                      Taux d'occupation
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayRows.map((row, i) => (
                    <TableRow
                      key={row.key}
                      sx={{ backgroundColor: i % 2 === 0 ? "#fafafa" : "#fff" }}
                    >
                      <TableCell sx={{ color: "#bf2f38" }}>{row.label}</TableCell>
                      <TableCell align="right">{row.excursionists}</TableCell>
                      <TableCell align="right">{row.destination}</TableCell>
                      <TableCell align="right">{row.total}</TableCell>
                      <TableCell align="right">{row.available}</TableCell>
                      <TableCell align="right">{row.occupied}</TableCell>
                      <TableCell align="right">{row.occupancy}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </FlexColumnBox>
  );
};

const headStyle = {
  fontSize: "1rem",
  backgroundColor: "#f5f5f5",
  color: "#bf2f38",
};

export default Traffic;
